import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, ViewChild, Input, EventEmitter, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { QuestionBase, DatetimeQuestion, NumberQuestion, Decorator } from 'core/components/forms';
import { ModalComponent } from '../modal/modal.component';
import { PopUpService, SubscriptionsService, ClientsService } from 'core/services/';
import { DateTime } from 'luxon';

@Component({
  selector: 'lms-extend',
  templateUrl: './extend.component.html',
  styleUrls: ['./extend.component.scss']
})
export class ExtendComponent implements OnInit {
  @Input() students: number = 1;
  @Input() options: any = {};
  @Output() dateSelected = new EventEmitter<Date>();
  @ViewChild('modalDateSelector', { static: false }) public modalDateSelector: ModalComponent;
  public weeks = 0;

  private newLicenseEndDate;
  private isCreditsClient: boolean = false;
  private baseDate;
  private minDate;
  private fundaeDateString;
  private fundaeDateHasExpired: boolean = false;
  private isDecoratorShown: boolean = false;

  private CREDITS_A_WEEK = 3;

  questions: QuestionBase<any>[] = [];

  constructor(
    public translate: TranslateService,
    public subscriptionsService: SubscriptionsService,
    public clientsService: ClientsService,
    public translateService: TranslateService,
    public popupService: PopUpService
  ) {}

  ngOnInit() {}

  get modalLabel() {
    return this.students > 1 ? 'extend' : 'extendSingle';
  }

  async openCalendar() {
    const { clientId, groupOriginalEndDate, currentEndDate, timezone, hasBonus } = this.options;

    await this.clientsService.getClientSubscriptionType(clientId).then((subscriptionType) => {
      this.isCreditsClient = subscriptionType === 'credits';
    });

    // currentEndDate is the greatest end date from all licenses selected
    const futureEndDate: boolean = currentEndDate.getTime() > Date.now();
    this.baseDate = futureEndDate ? currentEndDate : new Date();
    this.minDate = DateTime.fromJSDate(this.baseDate, { zone: timezone }).toJSDate();

    if (hasBonus) {
      this.fundaeDateHasExpired = groupOriginalEndDate <= new Date();
      const fundaeDate = DateTime.fromJSDate(groupOriginalEndDate, { zone: timezone }).toJSDate();
      this.fundaeDateString = `${fundaeDate.getFullYear()}-${
        fundaeDate.getMonth() + 1
      }-${fundaeDate.getDate()} ${fundaeDate.getHours()}:${fundaeDate.getMinutes()}`;
    }

    this.setQuestions();
    this.modalDateSelector.open();
  }

  setQuestions() {
    if (this.isCreditsClient) {
      this.questions = [this.getEndDateQuestion(), this.getExtensionWeeksQuestion()];
    } else {
      this.questions = [this.getEndDateQuestion()];
    }
  }

  getEndDateQuestion() {
    const { timezone } = this.options;
    return new DatetimeQuestion({
      key: 'endDate',
      label: 'tasks.licenses.newDateLabel',
      placeholder: '00/00/0000',
      class: 'col-1',
      minDate: this.minDate,
      defaultHour: 23,
      defaultMinute: 59,
      defaultSeconds: 59,
      timezone,
      validators: {
        required: true,
        invalidDate: true,
        function: (formControl: UntypedFormControl) => {
          const newLicenseEndDate = formControl.value;
          const isNewLicenseEndDateGreaterThanFuendaeDate =
            this.options.hasBonus && newLicenseEndDate > this.options.groupOriginalEndDate;
          const shouldShowErrorMessage = !this.fundaeDateHasExpired && isNewLicenseEndDateGreaterThanFuendaeDate;
          if (shouldShowErrorMessage) {
            return { maxEndDate: { date: this.fundaeDateString } };
          }
        }
      },
      eventHandlers: {
        onChange: (newLicenseEndDate) => {
          const shouldShowInfoMessage = this.options.hasBonus && this.fundaeDateHasExpired;
          if (!this.isDecoratorShown && shouldShowInfoMessage) {
            this.isDecoratorShown = true;
            this.questions.push(
              new Decorator({
                html: `<span class="glicon-alert-thin"></span><span>${this.translateService.instant(
                  'groups.extendMessageInfo',
                  {
                    date: this.fundaeDateString
                  }
                )}</span>`,
                valueClass: 'gl-forms__info'
              })
            );
          }
          if (this.isCreditsClient) {
            this.onLicenseEndDateChanged(newLicenseEndDate);
          }
        }
      }
    });
  }

  getExtensionWeeksQuestion(weeks = 1) {
    return new NumberQuestion({
      key: 'extensionWeeks',
      class: 'margin-top-input margin-bottom-none padding-right charcoal-grey-label',
      label: 'groups.playTime.extension.weeks',
      editable: false,
      value: weeks,
      validators: {
        minValue: { limit: 1 }
      }
    });
  }

  getCreditsCostInfoDecorator() {
    return new Decorator({
      label: this.translateService.instant('groups.playTime.extension.costInfo', {
        creditsAWeek: this.CREDITS_A_WEEK
      }),
      class: 'info-box margin-bottom',
      iconClass: 'mdi mdi-information-outline deep-sky-blue-text'
    });
  }

  onLicenseEndDateChanged(newLicenseEndDate) {
    this.newLicenseEndDate = newLicenseEndDate;
    const start = DateTime.fromJSDate(this.baseDate);
    const end = DateTime.fromJSDate(new Date(newLicenseEndDate));
    const diff = end.diff(start, 'weeks').toObject();
    const newWeeks = Math.ceil(diff.weeks);

    if (newWeeks !== this.weeks) {
      this.weeks = newWeeks;
      this.questions[1] = this.getExtensionWeeksQuestion(newWeeks);

      if (this.isCreditsClient) {
        if (this.isExtensionAlreadyPayedFor()) {
          // Hide credits cost info decorator
          if (this.questions.length > 2) {
            this.questions.pop();
          }
        } else {
          // Show credits cost info decorator
          if (this.questions.length < 3) {
            this.questions.push(this.getCreditsCostInfoDecorator());
          }
        }
      }
    }
  }

  private isExtensionAlreadyPayedFor() {
    const { extendableLicenses } = this.options;
    const newEndDate = DateTime.fromJSDate(new Date(this.newLicenseEndDate));

    for (const license of extendableLicenses) {
      if (license.paymentDate) {
        const paymentDate = DateTime.fromJSDate(new Date(license.paymentDate));
        const millisecondsDiff = newEndDate.diff(paymentDate).toObject().milliseconds;
        const isNewEndDateAfterPaymentDate = millisecondsDiff > 0;

        if (isNewEndDateAfterPaymentDate) {
          return false;
        }
      }
    }

    return true;
  }

  onSubmit(values) {
    const selectedDate = values.endDate;
    if (this.options.groupEndDate && selectedDate > new Date(this.options.groupEndDate)) {
      this.subscriptionsService.getClientSubscriptionExpiryDate(this.options.clientId).then((res) => {
        if (res.expiryDate) {
          const title = this.translate.instant(
            res.subscriptionType === 'credits'
              ? 'groups.expirationInfo.creditsTitle'
              : 'groups.expirationInfo.subscriptionTitle'
          );
          const text = this.translate.instant(
            res.subscriptionType === 'credits'
              ? 'groups.expirationInfo.creditsText'
              : 'groups.expirationInfo.subscriptionText'
          );
          // TODO Check for automatic subscriptions if/when they are implemented
          if (selectedDate.toISOString() > res.expiryDate) {
            this.popupService
              .showConfirm({
                title,
                text,
                type: 'info'
              })
              .then(() => {
                this.submitExtension(selectedDate);
              })
              .catch(() => {});
          } else {
            this.submitExtension(selectedDate);
          }
        } else {
          this.submitExtension(selectedDate);
        }
      });
    } else {
      this.submitExtension(selectedDate);
    }
  }

  submitExtension(selectedDate) {
    // if (this.isCreditsClient) {
    //   this.showExtensionCostConfirmAlert(selectedDate);
    // } else {
    //   this.dateSelected.emit(selectedDate);
    //   this.modalDateSelector.close();
    // }
    this.dateSelected.emit(selectedDate);
    this.closeModalDateSelector();
  }

  closeModalDateSelector() {
    this.isDecoratorShown = false;
    this.modalDateSelector.close();
  }

  async showExtensionCostConfirmAlert(selectedDate) {
    let swalHtml = '';
    let totalCredits = 0;
    const extendableLicenses = this.options.extendableLicenses;

    if (extendableLicenses) {
      for (const license of extendableLicenses) {
        let weeksToPay = 0;
        let creditsToPay = 0;
        const paymentDate = DateTime.fromJSDate(new Date(license.paymentDate));
        const newEndDate = DateTime.fromJSDate(this.newLicenseEndDate);

        weeksToPay = Math.ceil(newEndDate.diff(paymentDate, 'weeks').toObject().weeks);
        creditsToPay = weeksToPay > 0 ? this.CREDITS_A_WEEK * weeksToPay : 0;

        totalCredits += creditsToPay;

        let pendingWeeks = 0;
        const endDate = DateTime.fromJSDate(new Date(license.endDate));

        if (paymentDate && endDate && paymentDate < endDate) {
          pendingWeeks = Math.ceil(endDate.diff(paymentDate, 'weeks').toObject().weeks);
        }

        swalHtml = swalHtml.concat('<b>', license.userEmail, '</b>', '<br>');
        if (creditsToPay) {
          if (weeksToPay === 1) {
            swalHtml = swalHtml.concat(
              this.translateService.instant('groups.playTime.extension.costByWeek', {
                creditsAWeek: this.CREDITS_A_WEEK
              })
            );
          } else {
            if (pendingWeeks) {
              swalHtml = swalHtml.concat(
                this.translateService.instant('groups.playTime.extension.costByWeeksWithPending', {
                  weeks: weeksToPay,
                  credits: creditsToPay
                })
              );
            } else {
              swalHtml = swalHtml.concat(
                this.translateService.instant('groups.playTime.extension.costByWeeks', {
                  weeks: weeksToPay,
                  credits: creditsToPay
                })
              );
            }
          }
        }
        swalHtml = swalHtml.concat('<br>', '<br>');
      }

      this.subscriptionsService
        .checkCreditsAvailability(this.options.clientId, totalCredits)
        .then((res) => {
          if (res === true) {
            this.popupService
              .confirm({
                title: this.translateService.instant('groups.playTime.extension.confirmTitle', {
                  totalCredits
                }),
                html: swalHtml
              })
              .then(() => {
                this.dateSelected.emit(selectedDate);
                this.closeModalDateSelector();
              })
              .catch(() => {});
          }
        })
        .catch(() => {
          this.popupService
            .showError({
              title: this.translateService.instant('groups.playTime.extension.notEnoughCreditsTitle'),
              text: this.translateService.instant('groups.playTime.extension.notEnoughCreditsMessage', {
                credits: totalCredits
              })
            })
            .then(() => {
              this.closeModalDateSelector();
            });
        });
    } else {
      this.popupService
        .showWarning({
          title: this.translateService.instant('groups.playTime.extension.notPossible')
        })
        .then(() => {
          this.closeModalDateSelector();
        });
    }
  }
}
